import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Routine } from '@demind-inc/core';

import { schedulerApi } from '../api';
import { useAuthContext } from '../context';

interface UseSchedulerRoutineParams {
  routineId: string;
}

interface UseSchedulerRoutineQueryParams {
  queryKey: ['lifestack.scheduler.routine', UseSchedulerRoutineParams];
}

export const useSchedulerRoutine = ({ routineId }: UseSchedulerRoutineParams) => {
  const routineQuery = useQuery({
    queryKey: ['lifestack.scheduler.routine', { routineId }],
    queryFn: ({ queryKey }: UseSchedulerRoutineQueryParams) => {
      const [, { routineId }] = queryKey;
      return schedulerApi.getRoutine(routineId).then(({ data }) => data as Routine | undefined);
    },
    refetchOnWindowFocus: false,
    staleTime: 15000, // 15s
    enabled: !!routineId,
  });

  return {
    routine: routineQuery.data,
    ...routineQuery,
  };
};

/** Synchronous React Query cache read of the user's routine activities —
 * used by result_json routine_adjustments mapping (no network call). */
export const useRoutineCache = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  const findInCache = useCallback(() => {
    return (
      queryClient.getQueryData<Routine>([
        'lifestack.scheduler.routine',
        { routineId: user?.routineId },
      ])?.routineActivities ?? []
    );
  }, [queryClient, user?.routineId]);

  return {
    findInCache,
  };
};
